import Papa from 'papaparse';

/**
 * Constant for unknown month key
 */
export const UNKNOWN_MONTH_KEY = 'Unknown';

/**
 * Formats a file size in bytes to a human-readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.2 MB", "512 KB", "<1 KB")
 */
export const formatFileSize = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024) {
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    } else if (bytes >= 1024 * 1024) {
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    } else if (bytes >= 1024) {
        return (bytes / 1024).toFixed(1) + ' KB';
    } else {
        return '<1 KB';
    }
};

/**
 * Parses CSV headers from a header string using Papa Parse
 * @param {string} headerString - The CSV header string to parse
 * @returns {string[]} Array of parsed headers
 */
export const getHeaders = (headerString) => {
    if (!headerString) return [];
    
    const parseResult = Papa.parse(headerString, {
        header: false,
        skipEmptyLines: true
    });
    
    if (!parseResult.data || !parseResult.data[0]) return [];
    return parseResult.data[0].filter(h => h && h.trim() !== '');
};

/**
 * Extracts month from filename using YYYYMMDD or YYYY-MM-DD patterns
 * @param {string} fileName - The filename to extract date from
 * @returns {string} Month key in YYYY-MM format or UNKNOWN_MONTH_KEY
 */
export const extractMonthFromFilename = (fileName) => {
    // Extract month from filename - look for YYYYMMDD or YYYY-MM-DD patterns
    const dateMatch = fileName.match(/(\d{4})(\d{2})(\d{2})|(\d{4})-(\d{2})-(\d{2})/);
    
    if (dateMatch) {
        let year, month;
        if (dateMatch[1] && dateMatch[2] && dateMatch[3]) {
            // YYYYMMDD format
            year = dateMatch[1];
            month = dateMatch[2];
        } else if (dateMatch[4] && dateMatch[5] && dateMatch[6]) {
            // YYYY-MM-DD format
            year = dateMatch[4];
            month = dateMatch[5];
        }
        
        if (year && month) {
            return year + '-' + month;
        }
    }
    
    return UNKNOWN_MONTH_KEY;
};

/**
 * Groups files by month extracted from filename
 * @param {Array} uploadedFiles - Array of uploaded files
 * @param {Object} pendingFileMetadata - Pending file metadata object (optional)
 * @returns {Object} Object with month keys and file arrays as values
 */
export const groupFilesByMonth = (uploadedFiles, pendingFileMetadata) => {
    const groups = {};
    
    // Combine uploaded files with pending file
    const allFiles = [...uploadedFiles];
    if (pendingFileMetadata) {
        allFiles.push(pendingFileMetadata);
    }
    
    allFiles.forEach(file => {
        const monthKey = extractMonthFromFilename(file.file_name);
        
        if (!groups[monthKey]) {
            groups[monthKey] = [];
        }
        groups[monthKey].push(file);
    });
    
    return groups;
};

/**
 * Sorts month keys in descending order (latest first)
 * @param {Object} filesByMonth - Files grouped by month
 * @returns {Array} Sorted array of month keys
 */
export const getSortedMonths = (filesByMonth) => {
    return Object.keys(filesByMonth)
        .sort((a, b) => {
            // Handle 'Unknown' month - sort it to the end
            if (a === UNKNOWN_MONTH_KEY && b === UNKNOWN_MONTH_KEY) return 0;
            if (a === UNKNOWN_MONTH_KEY) return 1; // Unknown goes to the end
            if (b === UNKNOWN_MONTH_KEY) return -1; // Unknown goes to the end
            
            const [yearA, monthNumA] = a.split('-');
            const [yearB, monthNumB] = b.split('-');
            
            // Ensure we have valid year and month values
            if (!yearA || !monthNumA || !yearB || !monthNumB) {
                // If either is malformed, treat it like 'Unknown' and sort to end
                return a === b ? 0 : (a < b ? 1 : -1);
            }
            
            const decimalMonthA = parseInt(yearA) + parseInt(monthNumA) / 100;
            const decimalMonthB = parseInt(yearB) + parseInt(monthNumB) / 100;
            return decimalMonthB - decimalMonthA; // Descending order
        });
};

/**
 * Creates initial collapsed state for months (all collapsed except latest)
 * @param {Array} sortedMonths - Array of sorted month keys
 * @returns {Object} Collapsed state object
 */
export const createInitialCollapsedState = (sortedMonths) => {
    const collapsedState = {};
    sortedMonths.forEach((month, index) => {
        collapsedState[month] = index !== 0; // Collapse all except first (latest)
    });
    return collapsedState;
};